import React, { useState } from 'react';
import { MessageCircle, Mail, Phone, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const WHATSAPP_NUMBER = '+34678423475';
const EMAIL = 'carloscasallopez@gmail.com';
const TIKTOK_URL = 'https://www.tiktok.com/@eltiktokdeltuktuk?_r=1&_t=ZN-94YKV63s1N0';

const TikTokIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.88-2.88 2.89 2.89 0 0 1 2.88-2.88c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.72a8.2 8.2 0 0 0 4.76 1.52V6.79a4.83 4.83 0 0 1-1-.1z"/>
  </svg>
);

const CANCELLATION_POLICY = {
  es: {
    title: "Politica de cancelacion y condiciones",
    sections: [
      {
        title: "1. Politica de cancelacion",
        content: "Las cancelaciones realizadas por el cliente estaran sujetas a las siguientes condiciones:\n\n- Cancelaciones realizadas con mas de 15 dias de antelacion respecto a la fecha del tour: se procedera a la devolucion del 70% del importe total abonado.\n- Cancelaciones realizadas entre 15 dias y 48 horas antes del inicio del tour: se procedera a la devolucion del 50% del importe total abonado.\n- Cancelaciones realizadas con menos de 48 horas de antelacion al inicio del tour: no se realizara devolucion del importe abonado.\n\nLas devoluciones se efectuaran utilizando el mismo medio de pago utilizado en la reserva, salvo que se acuerde expresamente otro metodo."
      },
      {
        title: "2. Horario del servicio",
        content: "El servicio se prestara dentro del intervalo horario reservado por el cliente en el momento de la contratacion.\n\nLa reserva implica la contratacion de un periodo de tiempo determinado, por lo que la finalizacion del tour se mantendra en la hora prevista segun el horario reservado, con independencia de la hora exacta de inicio dentro de dicho intervalo."
      },
      {
        title: "3. Retrasos y no presentacion",
        content: "El proveedor del servicio esperara al cliente durante un plazo maximo de 30 minutos desde la hora de inicio prevista del tour.\n\nSi el cliente llega dentro de dicho plazo, el tour se realizara dentro del tiempo restante del periodo reservado.\n\nSi el cliente no se presenta en el punto de encuentro dentro de los 30 minutos posteriores a la hora de inicio, el servicio se considerara cancelado por no presentacion (no-show). En estos casos: la reserva se dara por cancelada automaticamente, el cliente perdera el derecho a la realizacion del tour, y no se realizara devolucion alguna del importe abonado."
      },
      {
        title: "4. Aceptacion de las condiciones",
        content: "La realizacion de una reserva a traves de la pagina web implica la aceptacion plena de la presente politica de cancelacion y de las condiciones de prestacion del servicio."
      }
    ]
  },
  en: {
    title: "Cancellation policy and conditions",
    sections: [
      {
        title: "1. Cancellation policy",
        content: "Cancellations made by the client are subject to the following conditions:\n\n- Cancellations made more than 15 days before the tour date: 70% of the total amount paid will be refunded.\n- Cancellations made between 15 days and 48 hours before the tour: 50% of the total amount paid will be refunded.\n- Cancellations made less than 48 hours before the tour: no refund will be issued.\n\nRefunds will be processed using the same payment method used for the booking."
      },
      {
        title: "2. Service schedule",
        content: "The service will be provided within the time slot booked by the client.\n\nThe booking implies the hiring of a specific time period, so the tour will end at the scheduled time regardless of the exact start time within that interval."
      },
      {
        title: "3. Delays and no-show",
        content: "The service provider will wait for the client for a maximum of 30 minutes from the scheduled start time.\n\nIf the client arrives within that period, the tour will take place within the remaining time.\n\nIf the client does not show up within 30 minutes, the service will be considered cancelled due to no-show. In such cases: the booking will be automatically cancelled, the client will lose the right to the tour, and no refund will be issued."
      },
      {
        title: "4. Acceptance of conditions",
        content: "Making a booking through the website implies full acceptance of this cancellation policy and service conditions."
      }
    ]
  },
  fr: {
    title: "Politique d'annulation et conditions",
    sections: [
      {
        title: "1. Politique d'annulation",
        content: "Les annulations effectuees par le client sont soumises aux conditions suivantes:\n\n- Annulations effectuees plus de 15 jours avant la date du tour: remboursement de 70% du montant total.\n- Annulations effectuees entre 15 jours et 48 heures avant le tour: remboursement de 50% du montant total.\n- Annulations effectuees moins de 48 heures avant le tour: aucun remboursement.\n\nLes remboursements seront effectues par le meme moyen de paiement utilise pour la reservation."
      },
      {
        title: "2. Horaire du service",
        content: "Le service sera assure dans le creneau horaire reserve par le client.\n\nLa reservation implique la location d'une periode de temps determinee."
      },
      {
        title: "3. Retards et non-presentation",
        content: "Le prestataire attendra le client pendant un maximum de 30 minutes a partir de l'heure de debut prevue.\n\nSi le client ne se presente pas dans les 30 minutes, le service sera considere comme annule. Aucun remboursement ne sera effectue."
      },
      {
        title: "4. Acceptation des conditions",
        content: "Effectuer une reservation implique l'acceptation pleine de cette politique d'annulation et des conditions de service."
      }
    ]
  }
};

const MODIFICATION_POLICY = {
  es: {
    title: "Politica de modificacion de reservas",
    sections: [
      {
        title: "1. Plazo para solicitar modificaciones",
        content: "Las solicitudes de modificacion de la reserva deberan realizarse con una antelacion minima de 48 horas respecto a la hora de inicio del tour. No se aceptaran solicitudes de modificacion dentro de las 48 horas previas al inicio del tour."
      },
      {
        title: "2. Alcance de las modificaciones",
        content: "Las modificaciones podran referirse exclusivamente a: cambio de fecha del tour, o cambio de horario, siempre que exista disponibilidad."
      },
      {
        title: "3. Limite de modificaciones",
        content: "Cada reserva podra ser modificada una unica vez, salvo autorizacion expresa del proveedor del servicio."
      },
      {
        title: "4. Procedimiento",
        content: "Las solicitudes de modificacion deberan realizarse a traves de los canales de contacto indicados en la pagina web. La modificacion solo se considerara confirmada una vez que el proveedor haya confirmado expresamente la nueva fecha u horario."
      }
    ]
  },
  en: {
    title: "Booking modification policy",
    sections: [
      {
        title: "1. Modification deadline",
        content: "Modification requests must be made at least 48 hours before the tour start time. Requests within 48 hours of the tour will not be accepted."
      },
      {
        title: "2. Scope of modifications",
        content: "Modifications may only refer to: change of tour date, or change of time, subject to availability."
      },
      {
        title: "3. Modification limit",
        content: "Each booking may be modified only once, unless expressly authorized by the service provider."
      },
      {
        title: "4. Procedure",
        content: "Modification requests must be made through the contact channels indicated on the website. The modification will only be considered confirmed once the provider has expressly confirmed the new date or time."
      }
    ]
  },
  fr: {
    title: "Politique de modification des reservations",
    sections: [
      {
        title: "1. Delai de modification",
        content: "Les demandes de modification doivent etre faites au moins 48 heures avant le debut du tour."
      },
      {
        title: "2. Portee des modifications",
        content: "Les modifications ne peuvent porter que sur: le changement de date ou d'horaire, sous reserve de disponibilite."
      },
      {
        title: "3. Limite de modifications",
        content: "Chaque reservation ne peut etre modifiee qu'une seule fois."
      },
      {
        title: "4. Procedure",
        content: "Les demandes doivent etre faites via les canaux de contact indiques sur le site web."
      }
    ]
  }
};

const Footer = () => {
  const { language, t } = useLanguage();
  const [showCancellation, setShowCancellation] = useState(false);
  const [showModification, setShowModification] = useState(false);

  const cancellation = CANCELLATION_POLICY[language] || CANCELLATION_POLICY.es;
  const modification = MODIFICATION_POLICY[language] || MODIFICATION_POLICY.es;

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer 
      className="bg-[#1C1917] text-white py-20"
      data-testid="footer"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand */}
          <div className="lg:col-span-2">
            <h3 className="font-display text-3xl font-bold mb-4">
              Madrid Tuk Tuk Tour
            </h3>
            <p className="text-white/70 mb-6 max-w-md">
              {t.footer.tagline}
            </p>
            {/* Social Links */}
            <div className="flex gap-4">
              <a 
                href={TIKTOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#C2410C] transition-colors"
                data-testid="social-tiktok"
              >
                <TikTokIcon />
              </a>
              <a 
                href={`https://wa.me/${WHATSAPP_NUMBER.replace(/[^0-9]/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#25D366] transition-colors"
                data-testid="social-whatsapp"
              >
                <MessageCircle className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-lg mb-6">{t.footer.tours}</h4>
            <ul className="space-y-3">
              <li>
                <button 
                  onClick={() => scrollToSection('#tours')}
                  className="text-white/70 hover:text-white transition-colors"
                >
                  Tour Express
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('#tours')}
                  className="text-white/70 hover:text-white transition-colors"
                >
                  Madrid, Capital de un Imperio
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('#tours')}
                  className="text-white/70 hover:text-white transition-colors"
                >
                  Madrid a Traves de los Siglos
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('#tours')}
                  className="text-white/70 hover:text-white transition-colors"
                >
                  Un Dia en Madrid
                </button>
              </li>
            </ul>
          </div>

          {/* Contact & Schedule */}
          <div>
            <h4 className="font-semibold text-lg mb-6">{t.footer.contact}</h4>
            <ul className="space-y-4">
              <li className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-[#C2410C]" />
                <span className="text-white/70">{WHATSAPP_NUMBER}</span>
              </li>
              <li>
                <a 
                  href={`mailto:${EMAIL}`}
                  className="flex items-center gap-3 text-white/70 hover:text-white transition-colors"
                  data-testid="footer-email-link"
                >
                  <Mail className="h-5 w-5 text-[#C2410C]" />
                  <span>Email</span>
                </a>
              </li>
            </ul>

            <h4 className="font-semibold text-lg mt-8 mb-4">{t.footer.schedule}</h4>
            <p className="text-white/70 text-sm">
              {t.footer.summerSchedule}<br />
              {t.footer.winterSchedule}<br />
              <span className="text-[#F59E0B]">{t.footer.everyDay}</span>
            </p>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/50 text-sm">
            &copy; {new Date().getFullYear()} Madrid Tuk Tuk Tour. {t.footer.rights}.
          </p>
          <div className="flex gap-6 text-sm text-white/50">
            <button 
              onClick={() => setShowCancellation(true)}
              className="hover:text-white transition-colors"
              data-testid="footer-cancellation-policy"
            >
              <FileText className="h-4 w-4 inline mr-1" />
              {language === 'en' ? 'Cancellation Policy' : language === 'fr' ? "Politique d'annulation" : 'Politica de Cancelacion'}
            </button>
            <button 
              onClick={() => setShowModification(true)}
              className="hover:text-white transition-colors"
              data-testid="footer-modification-policy"
            >
              <FileText className="h-4 w-4 inline mr-1" />
              {language === 'en' ? 'Modification Policy' : language === 'fr' ? 'Politique de modification' : 'Politica de Modificacion'}
            </button>
          </div>
        </div>
      </div>

      {/* Cancellation Policy Modal */}
      <Dialog open={showCancellation} onOpenChange={setShowCancellation}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{cancellation.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 text-sm text-[#57534E]">
            {cancellation.sections.map((section, idx) => (
              <div key={idx}>
                <h4 className="font-semibold text-[#1C1917] mb-2">{section.title}</h4>
                <p className="whitespace-pre-line leading-relaxed">{section.content}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modification Policy Modal */}
      <Dialog open={showModification} onOpenChange={setShowModification}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{modification.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 text-sm text-[#57534E]">
            {modification.sections.map((section, idx) => (
              <div key={idx}>
                <h4 className="font-semibold text-[#1C1917] mb-2">{section.title}</h4>
                <p className="whitespace-pre-line leading-relaxed">{section.content}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </footer>
  );
};

export default Footer;
